import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  ArrowDropDownRounded,
  ArrowForwardRounded,
  AttachMoneyRounded,
  CheckCircleRounded,
  DateRangeRounded,
  DonutLargeRounded,
  ErrorOutlineRounded,
  EventBusyRounded,
  EventRounded,
  FactCheckRounded,
  GroupsRounded,
  HourglassEmptyRounded,
  MoreHorizRounded,
  PersonOffRounded,
  PriceCheckRounded,
  RecordVoiceOverRounded,
  RefreshRounded,
  RequestPageRounded,
  RouteRounded,
  ScheduleRounded,
  ShieldOutlined,
  TaskAltRounded,
  TranslateRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "services/api";
import {
  getWorstCheckSeverity,
  loadValidationCheckConfig,
  runServiceChecks,
} from "helpers/serviceValidationChecks";
import WeekStrip from "components/WeekStrip";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";

// 2026-11-03 (Amine): dashboard tabs. Module scope so the identifier is stable
// and the tab-change callback never has to depend on a re-created array.
const DASHBOARD_TABS = ["today", "week", "next", "month"];

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard_NEW — the operations view (2026-10-30, Amine).
// This screen is deliberately NOT a report. Legacy / V2 answer "how did the
// selected month go?"; this one answers "what needs action right now?".
// Therefore: no month picker, no service filter, no period comparison. Every
// number comes from /dashboard/operations/summary, which is computed against
// today in the business timezone.
//
// Every signal is clickable and lands on the owning list pre-filtered to the
// exact rows that produced the number (`?ids=&months=` — the same contract the
// sidebar badges use). Nothing here is a dead end.
//
// Refresh strategy: the summary is cheap enough to refetch on mutation events
// (sockets), debounced so a burst of saves produces one request. The integrity
// scan ships a two-month row payload (~40 KB gzipped), so it is NOT refetched on
// every socket event — it loads once, refreshes with the manual button, and
// after a longer idle debounce.
// ─────────────────────────────────────────────────────────────────────────────

// Rows are evaluated by the REAL engine, so the Dashboard and the Checks column
// of every list can never disagree. The engine's category tokens differ from the
// services table literal ("Other" vs "OtherService"), hence the map.
const CHECK_CATEGORY = {
  Interpretation: "Interpretation",
  Translation: "Translation",
  Other: "OtherService",
};

// List route + label per category — the scan's deep links land on the list that
// owns the row, because `/services` does not accept `?ids=`.
const CATEGORY_TARGET = {
  Interpretation: { path: "/interpretations", label: "Interpretations", short: "I" },
  Translation: { path: "/translations", label: "Translations", short: "T" },
  OtherService: { path: "/otherServices", label: "Other Services", short: "O" },
};

// Fallback label for a check code the Settings config does not describe.
const humanizeCheckCode = (code) =>
  String(code || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (character) => character.toUpperCase());

// Mirrors SUMMARY_FILTER_MAX_IDS in the navbar/sidebar: ids beyond the cap are
// dropped, never truncated, so a link never filters to a partial set.
const buildListUrl = (path, filterRef) => {
  const ids = Array.isArray(filterRef?.ids) ? filterRef.ids : [];
  if (ids.length === 0) return path;

  const params = new URLSearchParams();
  params.set("ids", ids.join(","));

  const months = Array.isArray(filterRef?.months)
    ? filterRef.months.filter(Boolean)
    : [];
  if (months.length) params.set("months", months.join(","));

  return `${path}?${params.toString()}`;
};

// Month-only deep link — no ids. The three service lists accept `?months=` on
// its own (it just moves the visible month), which is what a period summary like
// "Interpretations closed — 92% of October" needs: the whole month, not a
// hand-picked id list.
const buildMonthUrl = (path, monthKeys) => {
  const months = (Array.isArray(monthKeys) ? monthKeys : [monthKeys])
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  if (months.length === 0) return path;

  const params = new URLSearchParams();
  params.set("months", months.join(","));
  return `${path}?${params.toString()}`;
};

const fmtInt = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));

// 2026-11-03 (Amine): cents are NOT optional on a money dashboard. The previous
// formatter pinned maximumFractionDigits to 0, so $16.94 rendered as "$17" and
// $1.41 as "$1" — which reads as though the stored value had been rounded when
// it had not. Always two decimals, which is also what the Linguist Payments
// screens use (`toFixed(2)` / minimumFractionDigits: 2), so a figure copied from
// here matches the figure there.
const fmtMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const fmtTime = (value) => {
  if (!value) return "";
  const parsed = dayjs(`2000-01-01T${String(value).slice(0, 8)}`);
  return parsed.isValid() ? parsed.format("h:mm A") : String(value);
};

const fmtDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(String(value).slice(0, 10));
  return parsed.isValid() ? parsed.format("MMM D") : String(value);
};

const MONTH_LABEL = (monthKey) => {
  const parsed = dayjs(`${monthKey}-01`);
  return parsed.isValid() ? parsed.format("MMM YYYY") : monthKey;
};

// ─── category split ─────────────────────────────────────────────────────────

/**
 * 2026-11-03 (Amine): the Dashboard must NEVER open `/services`. That route
 * aggregates all three categories but accepts no `?ids=`, so it can never show
 * the rows a figure was counted from. Every number here belongs to exactly one
 * of the three real lists — or to two of them at once.
 */
const normalizeCategoryOption = (entry) => {
  if (!entry) return null;

  const key = entry.category || entry.key;
  const target = CATEGORY_TARGET[key];
  const path = entry.path || target?.path;
  if (!path) return null;

  const count = Number(entry.count ?? entry.total ?? (entry.ids || []).length);
  if (!(count > 0)) return null;

  return {
    key,
    label: target?.label || entry.label || key,
    path,
    count,
    ids: Array.isArray(entry.ids) ? entry.ids : [],
    months: Array.isArray(entry.months) ? entry.months : [],
  };
};

/** Normalize, drop empties, widest first — the list holding most of the number leads. */
const CATEGORY_RANK = { Interpretation: 0, Translation: 1, OtherService: 2 };

const categoryOptionsOf = (entries) =>
  (entries || [])
    .map(normalizeCategoryOption)
    .filter(Boolean)
    // Ties fall back to the product's own category order (Interpretations,
    // Translations, Other Services) instead of row order, so the menu cannot
    // reshuffle between refreshes when two lists hold the same count.
    .sort(
      (a, b) =>
        b.count - a.count ||
        (CATEGORY_RANK[a.key] ?? 99) - (CATEGORY_RANK[b.key] ?? 99),
    );

// Ids win when present (exact rows); otherwise fall back to the month, which
// the service lists accept on its own. Without that fallback a month-scoped
// option would collapse to a bare path and silently lose its period.
const categoryOptionUrl = (option) => {
  const ids = Array.isArray(option.ids) ? option.ids : [];
  if (ids.length === 0 && (option.months || []).length > 0) {
    return buildMonthUrl(option.path, option.months);
  }
  return buildListUrl(option.path, { ids, months: option.months });
};

/**
 * The little "Open in" menu shared by chips and cards: one button per list,
 * each carrying its own count, so the split is visible BEFORE the click.
 */
const CategoryMenu = ({ anchorEl, options, onClose, onSelect }) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    slotProps={{
      paper: {
        sx: {
          mt: 0.75,
          minWidth: 240,
          // Never wider than the viewport on a phone, or the menu runs off-screen.
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "12px",
        },
      },
    }}
  >
    <ListSubheader
      sx={{
        lineHeight: 2.2,
        fontSize: "0.66rem",
        fontWeight: 800,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
      }}
    >
      Open in
    </ListSubheader>
    {options.map((option) => (
      <MenuItem
        key={option.key}
        onClick={() => onSelect(option)}
        sx={{ gap: 1.5, fontSize: "0.82rem", fontWeight: 600 }}
      >
        <Box sx={{ flex: 1, whiteSpace: "nowrap" }}>{option.label}</Box>
        <Chip
          size="small"
          label={fmtInt(option.count)}
          sx={{
            height: 20,
            minWidth: 32,
            borderRadius: "6px",
            fontWeight: 800,
            fontSize: "0.7rem",
          }}
        />
      </MenuItem>
    ))}
    {options.some((option) => option.ids.length === 0) ? (
      <Typography
        variant="caption"
        sx={{ display: "block", px: 2, py: 1, color: "text.secondary" }}
      >
        Too many rows to filter — that list opens unfiltered.
      </Typography>
    ) : null}
  </Menu>
);

/**
 * A chip whose number may live in one list or several.
 * One list → straight through. More than one → menu. Never `/services`.
 */
const SplitChip = ({ label, options = [], tone, onSelect, onClick }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const split = options.length > 1;
  const clickable = split || options.length === 1 || Boolean(onClick);

  const handleClick = (event) => {
    if (split) {
      setAnchorEl(event.currentTarget);
      return;
    }
    if (options.length === 1) {
      onSelect?.(options[0]);
      return;
    }
    onClick?.();
  };

  return (
    <>
      <Chip
        size="small"
        label={
          split ? (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
            >
              {label}
              <ArrowDropDownRounded sx={{ fontSize: 15, ml: -0.25 }} />
            </Box>
          ) : (
            label
          )
        }
        clickable={clickable}
        onClick={clickable ? handleClick : undefined}
        sx={{
          borderRadius: "8px",
          fontWeight: 600,
          height: 24,
          fontSize: "0.72rem",
          bgcolor: tone || theme.palette.action.hover,
          color: tone ? theme.palette.common.white : theme.palette.text.primary,
          "&:hover": { opacity: 0.9 },
        }}
      />
      {split ? (
        <CategoryMenu
          anchorEl={anchorEl}
          options={options}
          onClose={() => setAnchorEl(null)}
          onSelect={(option) => {
            setAnchorEl(null);
            onSelect?.(option);
          }}
        />
      ) : null}
    </>
  );
};

// ─── shared shells ──────────────────────────────────────────────────────────

const SectionTitle = ({ children, hint }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: { xs: 0.5, sm: 1 },
        flexWrap: "wrap",
        px: 0.5,
        // Trimmed from mt:1 — the section heading sits directly under the
        // TimeSection divider, and the two margins were stacking.
        mt: 0.5,
        minWidth: 0,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: theme.palette.text.primary }}
      >
        {children}
      </Typography>
      {hint ? (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            // The hint is explanatory, never essential — let it wrap under the
            // title on a phone instead of pushing the row wider than the screen.
            flexBasis: { xs: "100%", sm: "auto" },
          }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * TimeSection — the divider that turns the page into Today / This week /
 * Next week. Each band carries its own date range and its own summary chips so
 * a reader who scrolls straight to the middle still knows which week they are
 * looking at (a bare "6" means nothing without that context).
 */
const TimeSection = ({ label, range, caption, chips = [], accent }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: { xs: 0.75, sm: 1 },
        // 2026-11-03 (Amine): tightened from mt:{xs:2,sm:3} / pt:1.5. Those
        // values stacked with the sticky block's own pb and left ~44px of dead
        // space between the critical ribbon and the section heading.
        mt: { xs: 1, sm: 1.5 },
        mb: 0.5,
        px: 0.5,
        pt: 1,
        borderTop: `1px solid ${theme.palette.primary.border2}`,
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 26,
          borderRadius: 2,
          bgcolor: accent || theme.palette.primary.main,
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: { xs: "1rem", sm: "1.25rem" } }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, display: "block" }}
        >
          {range}
          {caption ? ` · ${caption}` : ""}
        </Typography>
      </Box>
      {/* The chip strip drops to its own line on a phone: the labels are the
          useful part and squeezing them beside the heading just truncates them. */}
      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        sx={{
          flexBasis: { xs: "100%", sm: "auto" },
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          minWidth: 0,
        }}
      >
        {chips.filter(Boolean).map((chip) => (
          <SplitChip
            key={chip.key}
            label={chip.label}
            options={chip.options}
            tone={chip.tone}
            onSelect={chip.onSelect}
            onClick={chip.onClick}
          />
        ))}
      </Stack>
    </Box>
  );
};

const OpsCard = ({
  icon,
  accent,
  title,
  value,
  valueColor,
  caption,
  onClick,
  footer,
  children,
  dense = false,
  // 2026-11-03 (Amine): when the number spans more than one list the click
  // opens a menu instead of guessing which list the reader meant.
  categoryOptions,
  onOpenCategory,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const options = categoryOptions || [];
  const hasSplit = options.length > 1;
  const isClickable = hasSplit || options.length === 1 || Boolean(onClick);

  const handleCardClick = (event) => {
    if (hasSplit) {
      setMenuAnchor(event.currentTarget);
      return;
    }
    if (options.length === 1) {
      onOpenCategory?.(options[0]);
      return;
    }
    onClick?.();
  };

  return (
    <>
      <Paper
        onClick={isClickable ? handleCardClick : undefined}
        sx={{
          p: { xs: 1.5, sm: 2 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.primary.border2}`,
          borderLeft: accent ? `4px solid ${accent}` : undefined,
          borderRadius: "12px",
          cursor: isClickable ? "pointer" : "default",
          transition: "all 0.2s ease",
          "&:hover": isClickable
            ? { boxShadow: theme.shadows[4], transform: "translateY(-2px)" }
            : undefined,
        }}
      >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {icon ? (
          <Box sx={{ color: accent || theme.palette.primary.main, display: "flex", pt: 0.25 }}>
            {icon}
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, fontWeight: 500, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          {value !== undefined && value !== null ? (
            <Typography
              variant={dense ? "h6" : "h5"}
              sx={{ fontWeight: 800, color: valueColor || theme.palette.text.primary, lineHeight: 1.25 }}
            >
              {value}
            </Typography>
          ) : null}
          {caption ? (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
        {isClickable ? (
          hasSplit ? (
            <ArrowDropDownRounded
              sx={{ fontSize: 18, color: theme.palette.text.secondary, opacity: 0.6 }}
            />
          ) : (
            <ArrowForwardRounded
              sx={{ fontSize: 18, color: theme.palette.text.secondary, opacity: 0.6 }}
            />
          )
        ) : null}
      </Box>

      {children}

      {footer ? (
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: "auto" }}>
          {footer}
        </Typography>
      ) : null}
      </Paper>

      {hasSplit ? (
        <CategoryMenu
          anchorEl={menuAnchor}
          options={options}
          onClose={() => setMenuAnchor(null)}
          onSelect={(option) => {
            setMenuAnchor(null);
            onOpenCategory?.(option);
          }}
        />
      ) : null}
    </>
  );
};

// A compact clickable row used inside the cards. Stop propagation so opening a
// profile does not also trigger the card's list navigation.
const MiniRow = ({ primary, secondary, trailing, trailingColor, onClick }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={(event) => {
        if (!onClick) return;
        event.stopPropagation();
        onClick();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 1 },
        px: { xs: 0.5, sm: 0.75 },
        // Comfortable touch target on a phone — the row is a link, so it needs
        // to be tappable without hitting its neighbour.
        py: { xs: 0.75, sm: 0.5 },
        borderRadius: "8px",
        minWidth: 0,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick
          ? { bgcolor: theme.palette.action.hover }
          : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          noWrap
          sx={{ display: "block", fontWeight: 600, color: theme.palette.text.primary }}
        >
          {primary}
        </Typography>
        {secondary ? (
          <Typography
            variant="caption"
            noWrap
            sx={{ display: "block", color: theme.palette.text.secondary, fontSize: "0.7rem" }}
          >
            {secondary}
          </Typography>
        ) : null}
      </Box>
      {trailing ? (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: trailingColor || theme.palette.text.secondary,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {trailing}
        </Typography>
      ) : null}
    </Box>
  );
};

// Stacked aging bar for the AR card — amounts, not counts, so the widest
// segment is where the money actually sits. 2026-11-03 (Amine): the bar is
// thicker and each band carries its own amount, so the split reads without
// hovering. Bands too narrow to hold a label keep the tooltip only.
const AgingBar = ({ segments, height = 30 }) => {
  const theme = useTheme();
  const total = segments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  if (total <= 0) return null;

  const bands = segments
    .map((segment) => {
      const amount = Number(segment.value || 0);
      return { ...segment, amount, width: total > 0 ? (amount / total) * 100 : 0 };
    })
    .filter((band) => band.width > 0);

  return (
    <Box
      sx={{
        display: "flex",
        height,
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {bands.map((band) => {
        // Under ~18% the label would be clipped mid-number ("$1,2…"), which is
        // worse than no label — those bands rely on the tooltip.
        const fits = band.width >= 18;
        const label = fmtMoney(band.amount);
        return (
          <Tooltip
            key={band.label}
            arrow
            // Same theme mapping as WeekStrip and the pie chart: MUI's global
            // tooltip is a hardcoded dark bubble, while `typography.caption`
            // forces `color: text2` on any caption inside it — in light mode
            // that is #616161 on #333333, effectively unreadable.
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  boxShadow: theme.shadows[2],
                },
              },
              arrow: { sx: { color: theme.palette.background.paper } },
            }}
            title={`${band.label}: ${label}`}
          >
            <Box
              sx={{
                width: `${band.width}%`,
                bgcolor: band.color,
                // 2026-11-03 (Amine): white bold label inside every band.
                color: theme.palette.common.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                px: fits ? 0.5 : 0,
              }}
            >
              {fits ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    // Slightly smaller on a phone: four bands on a ~320px bar
                    // leaves very little room per label.
                    fontSize: { xs: "0.625rem", sm: "0.6875rem" },
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    letterSpacing: "-0.01em",
                    color: theme.palette.common.white,
                  }}
                >
                  {label}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const EmptyLine = ({ children }) => {
  const theme = useTheme();
  return (
    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, px: 0.75 }}>
      {children}
    </Typography>
  );
};

// ─── main component ────────────────────────────────────────────────────────

const Dashboard_NEW = () => {
  usePageTitle("Dashboard");
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { refreshPage, handleErrorDialog } = useStateContext();
  const { socket } = useWebSocket();

  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // Week context: "what happened" (Mon → today) and "what is coming"
  // (next Mon → Sun), fetched together so the two halves cannot come from
  // different moments.
  const [weekly, setWeekly] = useState(null);
  const [isWeeklyLoading, setIsWeeklyLoading] = useState(true);

  // 2026-11-03 (Amine): the page is now four tabs instead of one long scroll.
  // Tab state lives in the URL so a refresh keeps the tab the user was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = String(searchParams.get("tab") || "today").toLowerCase();
  const activeTab = DASHBOARD_TABS.includes(requestedTab)
    ? requestedTab
    : "today";
  const handleTabChange = useCallback(
    (_event, nextTab) => {
      const next = DASHBOARD_TABS.includes(nextTab) ? nextTab : "today";
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "today") params.delete("tab");
          else params.set("tab", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Integrity scan: raw narrow rows + the two inputs the check engine needs.
  // `scanState` tracks its own lifecycle because it is heavier than the
  // summary and must never block the first paint.
  const [scanRows, setScanRows] = useState(null);
  const [scanWindow, setScanWindow] = useState(null);
  const [scanToday, setScanToday] = useState(null);
  const [checkConfig, setCheckConfig] = useState(null);
  const [mileageRates, setMileageRates] = useState([]);
  const [isScanLoading, setIsScanLoading] = useState(true);
  const [scanFailed, setScanFailed] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchSummary = useCallback(() => {
    setIsLoading(true);
    setError(null);

    api
      .get("/dashboard/operations/summary")
      .then((response) => {
        if (!isMountedRef.current) return;
        setSummary(response?.data || null);
        setIsLoading(false);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Unable to load the operations summary.";
        setError(message);
        setIsLoading(false);
        handleErrorDialog(message, theme);
      });
  }, [handleErrorDialog, theme]);

  // Integrity scan loader. Failure is deliberately QUIET (no error dialog): the
  // rest of the dashboard is still valid, and the card simply explains that the
  // scan could not run. Claiming "0 problems" would be worse than admitting the
  // check did not happen.
  const fetchIntegrityScan = useCallback(() => {
    setIsScanLoading(true);
    setScanFailed(false);

    // `loadValidationCheckConfig` is module-cached, so this is a no-op after the
    // first list screen of the session has loaded.
    Promise.all([
      api.get("/dashboard/operations/integrity-scan"),
      api.get("/mileageRates").catch(() => ({ data: [] })),
      loadValidationCheckConfig().catch(() => null),
    ])
      .then(([scanResponse, ratesResponse, config]) => {
        if (!isMountedRef.current) return;
        const payload = scanResponse?.data || null;
        setScanRows(Array.isArray(payload?.rows) ? payload.rows : null);
        setScanWindow(payload?.window || null);
        setScanToday(payload?.today || null);
        setMileageRates(Array.isArray(ratesResponse?.data) ? ratesResponse.data : []);
        setCheckConfig(Array.isArray(config) ? config : null);
        setIsScanLoading(false);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setScanRows(null);
        setScanFailed(true);
        setIsScanLoading(false);
      });
  }, []);

  // Week loader. A failure here must not take the page down — the Today
  // section is still valid, so the week blocks degrade to an inline message.
  const fetchWeekly = useCallback(() => {
    setIsWeeklyLoading(true);

    api
      .get("/dashboard/operations/weekly")
      .then((response) => {
        if (!isMountedRef.current) return;
        setWeekly(response?.data || null);
        setIsWeeklyLoading(false);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setWeekly(null);
        setIsWeeklyLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchWeekly();
  }, [fetchSummary, fetchWeekly, refreshPage]);

  useEffect(() => {
    fetchIntegrityScan();
  }, [fetchIntegrityScan, refreshPage]);

  // Live refresh: the same events the sidebar badges already listen to, plus
  // the generic entity/assignment broadcasts. Debounced so a save that emits
  // several events costs one request.
  useEffect(() => {
    if (!socket) return undefined;

    let summaryTimer = null;
    let scanTimer = null;
    let weeklyTimer = null;

    const scheduleRefresh = () => {
      if (summaryTimer) clearTimeout(summaryTimer);
      summaryTimer = setTimeout(() => {
        summaryTimer = null;
        fetchSummary();
      }, 1200);

      // Week blocks move slowly (a status change shifts a count by one), so a
      // longer debounce keeps a burst of saves from re-running seven queries.
      if (weeklyTimer) clearTimeout(weeklyTimer);
      weeklyTimer = setTimeout(() => {
        weeklyTimer = null;
        fetchWeekly();
      }, 8000);

      // The scan is intentionally slower to re-run: it re-ships two months of
      // rows and re-evaluates every check. A save you just made matters, but
      // ten saves in a row should cost one scan.
      if (scanTimer) clearTimeout(scanTimer);
      scanTimer = setTimeout(() => {
        scanTimer = null;
        fetchIntegrityScan();
      }, 45000);
    };

    const events = [
      "deadlineSummaryUpdated",
      "interpretationCompletionUpdated",
      "unbilledCountUpdated",
      "assignment:updated",
      "entity:updated",
      "task:updated",
    ];

    events.forEach((event) => socket.on(event, scheduleRefresh));

    return () => {
      if (summaryTimer) clearTimeout(summaryTimer);
      if (scanTimer) clearTimeout(scanTimer);
      if (weeklyTimer) clearTimeout(weeklyTimer);
      events.forEach((event) => socket.off(event, scheduleRefresh));
    };
  }, [socket, fetchSummary, fetchIntegrityScan, fetchWeekly]);

  // A falsy path is a deliberate no-op, not a dead link: `checkLink` returns
  // null when a check cannot be traced to a category.
  const go = useCallback(
    (path) => {
      if (path) navigate(path);
    },
    [navigate],
  );

  // Every list navigation goes through here — never `/services`.
  const openCategory = useCallback(
    (option) => go(categoryOptionUrl(option)),
    [go],
  );

  // 2026-11-03 (Amine): `/services/:id` is NOT a working profile route — each
  // service category has its own. The API payloads now carry `ServiceCategory`
  // on every item, so the route is derived from the row itself.
  //
  // `OtherService` is accepted as well as `Other`: the DB stores "Other" but the
  // check engine and the integrity scan speak in scene names ("OtherService"),
  // and one of those leaking into a payload used to produce a dead link to
  // /dashboard. Both literals resolve now, and an unknown category still falls
  // back to the dashboard rather than emitting a 404.
  const openService = useCallback(
    (id, serviceCategory) => {
      if (!id) return;

      const baseByCategory = {
        Translation: "/translations",
        Interpretation: "/interpretations",
        Other: "/otherServices",
        OtherService: "/otherServices",
      };
      const base = baseByCategory[String(serviceCategory || "")];

      navigate(base ? `${base}/${id}` : "/dashboard", {
        state: { ComingFrom: "/dashboard" },
      });
    },
    [navigate],
  );

  // ── derived: check-engine verdict for the scanned window ─────────────────
  // This is the SAME `runServiceChecks` the three service lists call, fed the
  // same context, so "Errors" on a list row and "errors" here always agree.
  const integrity = useMemo(() => {
    if (!Array.isArray(scanRows)) return null;

    const context = {
      mileageRates,
      // The engine expects the config rows the lists receive (nested
      // Conditions included) — `loadValidationCheckConfig` returns exactly that.
      checkConfig,
      // Business "today" from the server, so this card and the lists cannot
      // disagree across a midnight boundary.
      today: scanToday || undefined,
    };

    const byCode = new Map();
    const byCategory = {
      Interpretation: { errors: 0, warnings: 0, ids: [], months: new Set() },
      Translation: { errors: 0, warnings: 0, ids: [], months: new Set() },
      OtherService: { errors: 0, warnings: 0, ids: [], months: new Set() },
    };

    const rowsWithIssues = new Set();
    const rowsWithErrors = new Set();
    let errorCount = 0;
    let warningCount = 0;

    // Names come from Settings so a renamed check reads the same here as it
    // does in the Settings list; the humanized code is only a fallback.
    const nameByCode = new Map();
    if (Array.isArray(checkConfig)) {
      for (const row of checkConfig) {
        const code = String(row?.Code || "").trim().toLowerCase();
        if (code) nameByCode.set(code, String(row?.Name || "").trim());
      }
    }

    for (const row of scanRows) {
      const category = CHECK_CATEGORY[row?.ServiceCategory];
      if (!category) continue;

      const issues = runServiceChecks(row, category, context);
      if (issues.length === 0) continue;

      rowsWithIssues.add(row.id);
      if (getWorstCheckSeverity(issues) === "error") rowsWithErrors.add(row.id);

      const month = String(row.Date || "").slice(0, 7);
      let categoryTouched = false;

      for (const issue of issues) {
        const isError = issue.severity === "error";
        if (isError) errorCount += 1;
        else warningCount += 1;

        if (isError) byCategory[category].errors += 1;
        else byCategory[category].warnings += 1;
        categoryTouched = true;

        const key = String(issue.code || "").trim().toLowerCase();
        const entry =
          byCode.get(key) || {
            code: key,
            severity: issue.severity,
            count: 0,
            ids: [],
            months: new Set(),
            byCategory: {},
            sampleMessage: issue.message,
          };
        entry.count += 1;
        entry.ids.push(row.id);
        if (month) entry.months.add(month);
        entry.byCategory[category] = (entry.byCategory[category] || 0) + 1;
        // One error outranks any number of warnings for the chip colour.
        if (isError) entry.severity = "error";
        byCode.set(key, entry);
      }

      if (categoryTouched) {
        byCategory[category].ids.push(row.id);
        if (month) byCategory[category].months.add(month);
      }
    }

    const checks = [...byCode.values()]
      .map((entry) => {
        // The category carrying most of this check's hits — where a click
        // should land, and what the row's second line explains.
        const primaryCategory = Object.entries(entry.byCategory).sort(
          (a, b) => b[1] - a[1],
        )[0]?.[0];

        return {
          ...entry,
          label: nameByCode.get(entry.code) || humanizeCheckCode(entry.code),
          months: [...entry.months].sort(),
          primaryCategory,
          secondary: primaryCategory
            ? `${CATEGORY_TARGET[primaryCategory].label} · ${fmtInt(
                entry.byCategory[primaryCategory],
              )}${entry.severity === "error" ? " · error" : ""}`
            : entry.severity === "error"
              ? "error"
              : "warning",
        };
      })
      .sort((a, b) => {
        if (a.severity !== b.severity) return a.severity === "error" ? -1 : 1;
        return b.count - a.count;
      });

    const categories = Object.entries(byCategory)
      .map(([key, value]) => ({
        key,
        label: CATEGORY_TARGET[key].label,
        path: CATEGORY_TARGET[key].path,
        errors: value.errors,
        warnings: value.warnings,
        total: value.errors + value.warnings,
        ids: value.ids,
        months: [...value.months].sort(),
      }))
      .filter((entry) => entry.total > 0)
      .sort((a, b) => b.errors - a.errors || b.total - a.total);

    return {
      scannedRows: scanRows.length,
      rowsWithIssues: rowsWithIssues.size,
      rowsWithErrors: rowsWithErrors.size,
      errorCount,
      warningCount,
      checks,
      categories,
    };
  }, [scanRows, mileageRates, checkConfig, scanToday]);

  // ── derived: "This month" tab (2026-11-03, Amine) ─────────────────────────
  // Built ONLY from data this page already loads — the two-month integrity scan
  // and the summary — so there is no extra endpoint and no invented figure. The
  // scan window starts at the previous month, so the current month is always
  // fully covered.
  const monthStats = useMemo(() => {
    const businessToday = summary?.today || scanToday;
    if (!businessToday) return null;

    const anchor = dayjs(businessToday);
    const monthKey = anchor.format("YYYY-MM");
    const monthStart = anchor.startOf("month").format("YYYY-MM-DD");
    const monthEnd = anchor.endOf("month").format("YYYY-MM-DD");

    const rows = Array.isArray(scanRows) ? scanRows : [];
    const inMonth = rows.filter((row) => {
      const date = String(row?.Date || "").slice(0, 10);
      return date >= monthStart && date <= monthEnd;
    });

    const byCategory = { Interpretation: 0, Translation: 0, OtherService: 0 };
    let done = 0;
    let open = 0;
    let cancelled = 0;

    for (const row of inMonth) {
      const category = CHECK_CATEGORY[row?.ServiceCategory];
      if (category) byCategory[category] += 1;

      const status =
        row?.ServiceCategory === "Interpretation"
          ? row?.InterpretationStatus
          : row?.ServiceCategory === "Translation"
            ? row?.TranslationStatus
            : row?.OtherServiceStatus;
      const label = String(status || "");

      if (row?.Cancelled || /cancel/i.test(label)) cancelled += 1;
      else if (label === "Completed" || label === "Delivered") done += 1;
      else open += 1;
    }

    // Which checks fired INSIDE this month. `integrity.checks` already carries
    // the months each code touched, so this is a filter — not a second run of
    // the engine, and it cannot disagree with the Checks column.
    const checksThisMonth = (integrity?.checks || []).filter((check) =>
      (check.months || []).includes(monthKey),
    );

    return {
      label: anchor.format("MMMM YYYY"),
      monthKey,
      total: inMonth.length,
      done,
      open,
      cancelled,
      byCategory,
      checksThisMonth,
      errorChecks: checksThisMonth.filter((c) => c.severity === "error").length,
      warningChecks: checksThisMonth.filter((c) => c.severity === "warning")
        .length,
    };
  }, [scanRows, summary?.today, scanToday, integrity]);

  // Deep link for one check: its own ids, in the category that carries most of
  // them (each row has exactly one category, so the ids stay valid). No
  // `/services` fallback — an untraceable check is simply not clickable.
  const checkLink = useCallback((entry) => {
    const category = entry?.primaryCategory;
    if (!category || !CATEGORY_TARGET[category]) return null;
    return buildListUrl(CATEGORY_TARGET[category].path, {
      ids: entry.ids,
      months: entry.months,
    });
  }, []);

  // ── derived: attention signals (ordered by consequence) ──────────────────
  const signals = useMemo(() => {
    if (!summary) return [];
    const list = [];
    const { deadlines, interpretations, hygiene, money, schedule } = summary;

    if (deadlines?.totals?.pastDue > 0) {
      // Translations and Other Services are two different lists; a late
      // Translation does not excuse the reader from choosing.
      const pastDueOptions = categoryOptionsOf([
        ...(deadlines.translations.byCategory || []),
        ...(deadlines.other.byCategory || []),
      ]);

      list.push({
        key: "pastDue",
        severity: "error",
        label: `${fmtInt(deadlines.totals.pastDue)} past due`,
        options: pastDueOptions,
        onSelect: openCategory,
        // Only used if the server ever stops sending the split.
        onClick: () => {
          const useOther = deadlines.other.oldestDays > deadlines.translations.oldestDays;
          go(
            buildListUrl(
              useOther ? "/otherServices" : "/translations",
              useOther
                ? deadlines.other.filters.pastDue
                : deadlines.translations.filters.pastDue,
            ),
          );
        },
      });
    }

    if (interpretations?.unassigned?.count > 0) {
      list.push({
        key: "unassigned",
        severity: "error",
        label: `${fmtInt(interpretations.unassigned.count)} with no linguist`,
        onClick: () =>
          go(buildListUrl("/interpretations", interpretations.unassigned.filters)),
      });
    }

    if (interpretations?.unclosed?.count > 0) {
      list.push({
        key: "unclosed",
        severity: "error",
        label: `${fmtInt(interpretations.unclosed.count)} unclosed`,
        onClick: () =>
          go(buildListUrl("/interpretations", interpretations.unclosed.filters)),
      });
    }

    if (money?.ar?.count > 0) {
      list.push({
        key: "ar",
        severity: "error",
        label: `${fmtMoney(money.ar.amount)} overdue in AR`,
        onClick: () => go("/invoices"),
      });
    }

    if (money?.unbilled?.count > 0) {
      list.push({
        key: "unbilled",
        severity: "warning",
        label: `${fmtInt(money.unbilled.count)} unbilled`,
        onClick: () => go("/invoices"),
      });
    }

    if (hygiene?.noDeadline?.count > 0) {
      list.push({
        key: "noDeadline",
        severity: "warning",
        label: `${fmtInt(hygiene.noDeadline.count)} without a deadline`,
        options: categoryOptionsOf(hygiene.noDeadline.byCategory),
        onSelect: openCategory,
      });
    }

    // Data integrity: rows the automatic checks flag. This is the one signal
    // that was previously invisible unless you opened a list and read the
    // Checks column row by row.
    if (integrity?.rowsWithErrors > 0) {
      const worst = integrity.categories.find((entry) => entry.errors > 0);
      list.push({
        key: "integrity",
        severity: "error",
        label: `${fmtInt(integrity.rowsWithErrors)} with data errors`,
        // Count rows, not issues: the label counts rows too, so the menu and
        // the chip can never disagree.
        options: categoryOptionsOf(
          integrity.categories
            .filter((entry) => entry.errors > 0)
            .map((entry) => ({ ...entry, count: entry.ids.length })),
        ),
        onSelect: openCategory,
        onClick: () =>
          go(worst ? buildListUrl(worst.path, { ids: worst.ids, months: worst.months }) : null),
      });
    } else if (integrity?.rowsWithIssues > 0) {
      const worst = integrity.categories[0];
      list.push({
        key: "integrity",
        severity: "warning",
        label: `${fmtInt(integrity.rowsWithIssues)} with check warnings`,
        options: categoryOptionsOf(
          integrity.categories.map((entry) => ({ ...entry, count: entry.ids.length })),
        ),
        onSelect: openCategory,
        onClick: () =>
          go(worst ? buildListUrl(worst.path, { ids: worst.ids, months: worst.months }) : null),
      });
    }

    if (hygiene?.pendingChecks?.total > 0) {
      list.push({
        key: "checks",
        severity: "info",
        label: `${fmtInt(hygiene.pendingChecks.total)} unchecked`,
        options: categoryOptionsOf(hygiene.pendingChecks.byCategory),
        onSelect: openCategory,
      });
    }

    if (schedule?.unassignedCount > 0) {
      list.push({
        key: "todayUnassigned",
        severity: "warning",
        label: `${fmtInt(schedule.unassignedCount)} today unassigned`,
        // Filtered to today's unassigned rows when the server supplies them,
        // otherwise the plain list — never a link that does nothing.
        onClick: () =>
          go(buildListUrl("/interpretations", schedule.unassigned?.filters)),
      });
    }

    return list;
  }, [summary, go, openCategory, integrity]);

  const criticalSignals = signals.filter((signal) => signal.severity === "error");
  const warningSignals = signals.filter((signal) => signal.severity === "warning");

  const ribbonTone = criticalSignals.length
    ? { color: theme.palette.error.main, bg: theme.palette.background.error, icon: <ErrorOutlineRounded /> }
    : warningSignals.length
      ? { color: theme.palette.warning.main, bg: theme.palette.background.warning, icon: <WarningAmberRounded /> }
      : { color: theme.palette.success.main, bg: theme.palette.background.default, icon: <CheckCircleRounded /> };

  // 2026-11-03 (Amine): the "Updated 1:50 PM · America/New_York" label was
  // removed at Amine's request, so the timestamp is no longer derived here.

  // ── render helpers ───────────────────────────────────────────────────────
  // PROGRESSIVE FILL-IN (2026-11-03, Amine). The page used to gate EVERYTHING
  // on the slowest request: one full-page skeleton until
  // `/dashboard/operations/summary` returned, so even the tab bar did not exist
  // yet and the screen read as frozen. Now the chrome paints on the first frame
  // and each section swaps its own skeleton for real content as its data lands.
  const renderCardSkeletons = (count = 3, height = 148) => (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {[...Array(count)].map((_, index) => (
        <Grid key={index} size={{ xs: 12, sm: 6, lg: 4 }}>
          <Skeleton
            variant="rounded"
            height={height}
            sx={{ borderRadius: "12px" }}
            animation="wave"
          />
        </Grid>
      ))}
    </Grid>
  );

  const renderDeadlineCard = (bucket, config) => {
    const data = summary.deadlines?.[bucket] || {};
    const hasPastDue = Number(data.pastDue || 0) > 0;
    const targetFilter = hasPastDue
      ? data.filters?.pastDue
      : Number(data.dueToday || 0) > 0
        ? data.filters?.dueToday
        : data.filters?.next7;

    return (
      <OpsCard
        key={bucket}
        icon={config.icon}
        accent={hasPastDue ? theme.palette.error.main : theme.palette.success.main}
        title={config.title}
        value={fmtInt(data.pastDue)}
        valueColor={hasPastDue ? theme.palette.error.main : theme.palette.success.main}
        caption={
          hasPastDue
            ? `past due · oldest ${fmtInt(data.oldestDays)} days late`
            : `Nothing past due · ${fmtInt(data.dueToday)} due today`
        }
        onClick={() => go(buildListUrl(config.path, targetFilter))}
        footer={`${fmtInt(data.dueToday)} due today · ${fmtInt(data.next7)} in the next 7 days`}
      >
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {(data.items || []).length === 0 ? (
            <EmptyLine>No open deadline work in the next 7 days.</EmptyLine>
          ) : (
            data.items.map((item) => (
              <MiniRow
                key={item.id}
                primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                secondary={`${fmtDate(item.Deadline)} · ${item.Name || "Untitled"}`}
                trailing={
                  item.daysLate > 0
                    ? `${fmtInt(item.daysLate)}d late`
                    : item.bucket === "dueToday"
                      ? "Today"
                      : "Soon"
                }
                trailingColor={
                  item.daysLate > 0 ? theme.palette.error.main : theme.palette.warning.main
                }
                onClick={() => openService(item.id, item.ServiceCategory)}
              />
            ))
          )}
        </Stack>
      </OpsCard>
    );
  };

  const renderMoney = () => {
    // Skeletons while the summary is still landing; `null` only when the server
    // deliberately omits money (Members) — that is a real "not for you", not a
    // pending state.
    if (!summary) {
      return (
        <>
          <SectionTitle hint="cash already earned or committed">Money at risk</SectionTitle>
          {renderCardSkeletons(3, 172)}
        </>
      );
    }
    if (!summary?.money) return null;
    const { unbilled, ar, linguistPay } = summary.money;

    return (
      <>
        <SectionTitle hint="cash already earned or committed">Money at risk</SectionTitle>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<AttachMoneyRounded />}
              accent={unbilled.count > 0 ? theme.palette.warning.main : theme.palette.success.main}
              title="Unbilled from prior months"
              value={fmtMoney(unbilled.amount)}
              valueColor={unbilled.count > 0 ? theme.palette.warning.main : theme.palette.success.main}
              caption={
                unbilled.count > 0
                  ? `${fmtInt(unbilled.count)} assignments · oldest ${fmtInt(unbilled.oldestDays)} days`
                  : "Everything from prior months is invoiced"
              }
              // 2026-11-03 (Amine): these are assignments, not invoices — nothing
              // has been billed yet, so /invoices could never show them. One
              // button per service list, each opening the exact rows.
              categoryOptions={categoryOptionsOf(unbilled.byCategory)}
              onOpenCategory={openCategory}
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(unbilled.items || []).slice(0, 3).map((item) => (
                  <MiniRow
                    key={item.id}
                    primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                    secondary={`${fmtDate(item.Date)} · ${fmtInt(item.daysOld)} days old`}
                    trailing={fmtMoney(item.Amount)}
                    trailingColor={theme.palette.warning.main}
                    onClick={() => openService(item.id, item.ServiceCategory)}
                  />
                ))}
                {unbilled.count === 0 ? <EmptyLine>Nothing waiting to be billed.</EmptyLine> : null}
              </Stack>
            </OpsCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<RequestPageRounded />}
              accent={ar.count > 0 ? theme.palette.error.main : theme.palette.success.main}
              title="Invoices past due"
              value={fmtMoney(ar.amount)}
              valueColor={ar.count > 0 ? theme.palette.error.main : theme.palette.success.main}
              caption={
                ar.count > 0
                  ? `${fmtInt(ar.count)} invoices · oldest ${fmtInt(ar.oldestDays)} days late`
                  : "No invoice is past its due date"
              }
              onClick={() => go("/invoices")}
            >
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {ar.count > 0 ? (
                  <AgingBar
                    segments={[
                      { label: "1–30 days", value: ar.buckets?.d1_30, color: theme.palette.info.main },
                      { label: "31–60 days", value: ar.buckets?.d31_60, color: theme.palette.warning.main },
                      { label: "61–90 days", value: ar.buckets?.d61_90, color: theme.palette.error[400] },
                      { label: "90+ days", value: ar.buckets?.d90_plus, color: theme.palette.error[800] },
                    ]}
                  />
                ) : null}
                {(ar.items || []).slice(0, 2).map((invoice) => (
                  <MiniRow
                    key={invoice.id}
                    primary={`#${invoice.Number ?? "—"} · ${invoice.ClientName}`}
                    secondary={`Due ${fmtDate(invoice.DueDate)}`}
                    trailing={`${fmtInt(invoice.daysPastDue)}d · ${fmtMoney(invoice.Amount)}`}
                    trailingColor={theme.palette.error.main}
                  />
                ))}
                {ar.count === 0 ? <EmptyLine>Collections are current.</EmptyLine> : null}
              </Stack>
            </OpsCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<PriceCheckRounded />}
              accent={linguistPay.amount > 0 ? theme.palette.info.main : theme.palette.success.main}
              title="Linguist pay owed"
              value={fmtMoney(linguistPay.amount)}
              valueColor={linguistPay.amount > 0 ? theme.palette.text.primary : theme.palette.success.main}
              caption={
                linguistPay.count > 0
                  ? `${fmtInt(linguistPay.count)} delivered assignments · oldest ${fmtInt(linguistPay.oldestDays)} days`
                  : "No delivered work is awaiting payment"
              }
              // 2026-11-03 (Amine): opens Linguist Payments filtered to these
              // exact assignments — the same id contract every other card uses.
              onClick={() =>
                go(buildListUrl("/linguistsPayments", linguistPay.filters))
              }
              footer="Delivered / Completed work only — in-progress jobs are not counted."
            >
              {linguistPay.count > 0 ? (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {(linguistPay.items || []).map((item) => (
                    <MiniRow
                      key={item.id}
                      primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                      secondary={`${fmtDate(item.Date)} · ${item.daysOld} days old`}
                      trailing={fmtMoney(item.Amount)}
                      trailingColor={theme.palette.info.main}
                      onClick={() => openService(item.id, item.ServiceCategory)}
                    />
                  ))}
                  {linguistPay.itemsTruncated ? (
                    <EmptyLine>
                      +{fmtInt(linguistPay.count - (linguistPay.items || []).length)} more
                      — open the card for the full list.
                    </EmptyLine>
                  ) : null}
                </Stack>
              ) : null}
            </OpsCard>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderCommitments = () => {
    // `renderDeadlineCard` dereferences `summary.deadlines` without a guard, so
    // this section could only exist once the summary had arrived — rendering a
    // skeleton instead of returning early is what lets the rest of the page
    // paint immediately.
    if (!summary) {
      return (
        <>
          <SectionTitle hint="promises already made to clients">Client commitments</SectionTitle>
          {renderCardSkeletons(3, 172)}
        </>
      );
    }
    const unclosed = summary?.interpretations?.unclosed || {};

    return (
      <>
        <SectionTitle hint="promises already made to clients">Client commitments</SectionTitle>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            {renderDeadlineCard("translations", {
              title: "Translations past due",
              path: "/translations",
              icon: <TranslateRounded />,
            })}
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            {renderDeadlineCard("other", {
              title: "Other services past due",
              path: "/otherServices",
              icon: <MoreHorizRounded />,
            })}
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<RecordVoiceOverRounded />}
              accent={unclosed.count > 0 ? theme.palette.error.main : theme.palette.success.main}
              title="Interpretations never closed"
              value={fmtInt(unclosed.count)}
              valueColor={unclosed.count > 0 ? theme.palette.error.main : theme.palette.success.main}
              caption={
                unclosed.count > 0
                  ? `Requested or Confirmed, date already passed · oldest ${fmtInt(unclosed.oldestDays)} days`
                  : "Every past interpretation is Completed"
              }
              onClick={() =>
                go(
                  buildListUrl("/interpretations", unclosed.filters || { ids: [] }),
                )
              }
              footer={
                (unclosed.byMonth || []).length
                  ? (unclosed.byMonth || [])
                      .map((entry) => `${MONTH_LABEL(entry.month)} · ${entry.count}`)
                      .join("   ")
                  : "These are invoiced only once marked Completed."
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(unclosed.items || []).slice(0, 3).map((item) => (
                  <MiniRow
                    key={item.id}
                    primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                    secondary={`${fmtDate(item.Date)} · ${item.Status}`}
                    trailing={`${fmtInt(item.daysPast)}d`}
                    trailingColor={theme.palette.error.main}
                    onClick={() => openService(item.id, item.ServiceCategory)}
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>
        </Grid>
      </>
    );
  };

  // ── data integrity ───────────────────────────────────────────────────────
  // Runs the same engine the lists use, over the last two months of work. The
  // two panels answer different questions: "how bad is it, per team?" and
  // "which rule is firing most?" — nobody could answer the second one before
  // without opening each list and reading the Checks column.
  const renderIntegrity = () => {
    const windowLabel = scanWindow
      ? `${MONTH_LABEL(String(scanWindow.from).slice(0, 7))} → today`
      : "recent work";

    if (isScanLoading && !integrity) {
      return (
        <>
          <SectionTitle hint="automatic checks on recent work">Data integrity</SectionTitle>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <Skeleton variant="rounded" height={132} sx={{ borderRadius: "12px" }} animation="wave" />
            </Grid>
          </Grid>
        </>
      );
    }

    if (scanFailed || !integrity) {
      return (
        <>
          <SectionTitle hint="automatic checks on recent work">Data integrity</SectionTitle>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <OpsCard
                icon={<ShieldOutlined />}
                accent={theme.palette.text.secondary}
                title="Integrity scan unavailable"
                value="—"
                caption="The check scan could not run. The lists' Checks column is still accurate."
                onClick={fetchIntegrityScan}
                footer="Click to retry."
              />
            </Grid>
          </Grid>
        </>
      );
    }

    const hasProblems = integrity.rowsWithIssues > 0;
    const accent = integrity.rowsWithErrors
      ? theme.palette.error.main
      : hasProblems
        ? theme.palette.warning.main
        : theme.palette.success.main;

    return (
      <>
        <SectionTitle hint="automatic checks on recent work">Data integrity</SectionTitle>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <OpsCard
              icon={<ShieldOutlined />}
              accent={accent}
              title="Assignments with failed checks"
              value={fmtInt(integrity.rowsWithIssues)}
              valueColor={accent}
              caption={
                hasProblems
                  ? `${fmtInt(integrity.errorCount)} errors · ${fmtInt(integrity.warningCount)} warnings across ${fmtInt(integrity.scannedRows)} scanned`
                  : `All ${fmtInt(integrity.scannedRows)} scanned assignments pass`
              }
              footer={
                hasProblems
                  ? "Same rules as the Checks column — click a team to open exactly those rows."
                  : "Nothing to fix in this window."
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {integrity.categories.length === 0 ? (
                  <EmptyLine>No assignment in {windowLabel} has a failed check.</EmptyLine>
                ) : (
                  integrity.categories.map((entry) => (
                    <MiniRow
                      key={entry.key}
                      primary={entry.label}
                      secondary={
                        entry.errors > 0
                          ? `${fmtInt(entry.errors)} errors · ${fmtInt(entry.warnings)} warnings`
                          : `${fmtInt(entry.warnings)} warnings`
                      }
                      trailing={fmtInt(entry.total)}
                      trailingColor={
                        entry.errors > 0 ? theme.palette.error.main : theme.palette.warning.main
                      }
                      onClick={() =>
                        go(buildListUrl(entry.path, { ids: entry.ids, months: entry.months }))
                      }
                    />
                  ))
                )}
              </Stack>
            </OpsCard>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <OpsCard
              icon={<FactCheckRounded />}
              accent={hasProblems ? accent : theme.palette.success.main}
              title="Most frequent issues"
              caption={windowLabel}
              footer={
                integrity.checks.length > 0
                  ? "Click an issue to open the rows it flagged."
                  : undefined
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {integrity.checks.length === 0 ? (
                  <EmptyLine>Every configured check passed.</EmptyLine>
                ) : (
                  integrity.checks.slice(0, 5).map((entry) => (
                    <MiniRow
                      key={entry.code}
                      primary={entry.label}
                      secondary={entry.secondary || undefined}
                      trailing={fmtInt(entry.count)}
                      trailingColor={
                        entry.severity === "error"
                          ? theme.palette.error.main
                          : theme.palette.warning.main
                      }
                      onClick={() => go(checkLink(entry))}
                    />
                  ))
                )}
              </Stack>
            </OpsCard>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderAboutToBreak = () => {
    if (!summary) {
      return (
        <>
          <SectionTitle hint="not late yet — fix these before they are">About to break</SectionTitle>
          {renderCardSkeletons(3, 172)}
        </>
      );
    }
    const unassigned = summary?.interpretations?.unassigned || {};
    const noDeadline = summary?.hygiene?.noDeadline || {};
    const pendingChecks = summary?.hygiene?.pendingChecks || { total: 0 };

    return (
      <>
        <SectionTitle hint="not late yet — fix these before they are">About to break</SectionTitle>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<PersonOffRounded />}
              accent={unassigned.count > 0 ? theme.palette.error.main : theme.palette.success.main}
              title="Confirmed, no linguist"
              value={fmtInt(unassigned.count)}
              valueColor={unassigned.count > 0 ? theme.palette.error.main : theme.palette.success.main}
              caption="next 14 days"
              onClick={() =>
                go(buildListUrl("/interpretations", unassigned.filters || { ids: [] }))
              }
              footer={
                unassigned.count === 0
                  ? "Everyone scheduled has a linguist."
                  : "An assignment with no linguist cannot be delivered."
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(unassigned.items || []).slice(0, 3).map((item) => (
                  <MiniRow
                    key={item.id}
                    primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                    secondary={`${fmtDate(item.Date)}${item.TimeStart ? ` · ${fmtTime(item.TimeStart)}` : ""}`}
                    trailing={item.daysAway === 0 ? "Today" : `in ${fmtInt(item.daysAway)}d`}
                    trailingColor={theme.palette.error.main}
                    onClick={() => openService(item.id, item.ServiceCategory)}
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<EventBusyRounded />}
              accent={noDeadline.count > 0 ? theme.palette.warning.main : theme.palette.success.main}
              title="No deadline set"
              value={fmtInt(noDeadline.count)}
              valueColor={noDeadline.count > 0 ? theme.palette.warning.main : theme.palette.success.main}
              caption="open work that can never be flagged late"
              categoryOptions={categoryOptionsOf(noDeadline.byCategory)}
              onOpenCategory={openCategory}
              footer={
                noDeadline.count === 0
                  ? "Every open assignment has a deadline."
                  : "Set a deadline so the past-due lists can see it."
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(noDeadline.items || []).slice(0, 3).map((item) => (
                  <MiniRow
                    key={item.id}
                    primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                    secondary={`${item.ServiceCategory === "Translation" ? "Translation" : "Other service"} · ${fmtDate(item.Date)}`}
                    onClick={() => openService(item.id, item.ServiceCategory)}
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<FactCheckRounded />}
              accent={pendingChecks.total > 0 ? theme.palette.info.main : theme.palette.success.main}
              title="Unchecked this month"
              value={fmtInt(pendingChecks.total)}
              valueColor={pendingChecks.total > 0 ? theme.palette.info.main : theme.palette.success.main}
              caption="assignments nobody has reviewed yet"
              categoryOptions={categoryOptionsOf(pendingChecks.byCategory)}
              onOpenCategory={openCategory}
              footer={`Interpretations ${fmtInt(pendingChecks.interpretation)} · Translations ${fmtInt(pendingChecks.translation)} · Other ${fmtInt(pendingChecks.other)}`}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  // ── TODAY: my tasks ───────────────────────────────────────────────────────
  // The Tasks board only ever told you what you owed if you went and opened it.
  // Split "mine" from "team" deliberately: a Member should see their own queue
  // first, and an Admin still sees the team backlog without it drowning theirs.
  const renderMyTasks = () => {
    const tasks = weekly?.tasks;
    // Distinguish "not loaded yet" from "you genuinely have nothing due":
    // the section hides itself in the second case, but a silent nothing while
    // loading is what made the page feel stuck.
    if (!tasks) {
      return weekly ? null : (
        <>
          <SectionTitle hint="from the Tasks board">Your queue</SectionTitle>
          {renderCardSkeletons(2, 132)}
        </>
      );
    }

    const mineOverdue = tasks.mine?.overdue || [];
    const mineToday = tasks.mine?.dueToday || [];
    const mineCount = mineOverdue.length + mineToday.length;
    const team = tasks.team || {};

    // Nothing of mine and nothing overdue team-wide → stay out of the way.
    if (mineCount === 0 && !team.overdue) return null;

    const tone =
      mineOverdue.length > 0 || team.overdue > 0
        ? theme.palette.error.main
        : theme.palette.warning.main;

    return (
      <>
        <SectionTitle hint="from the Tasks board">Your queue</SectionTitle>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<TaskAltRounded />}
              accent={tone}
              title="Tasks needing you"
              value={fmtInt(mineCount)}
              valueColor={mineCount > 0 ? tone : theme.palette.success.main}
              caption={
                mineCount > 0
                  ? `${fmtInt(mineOverdue.length)} overdue · ${fmtInt(mineToday.length)} due today`
                  : "Nothing assigned to you is due"
              }
              onClick={() => go("/tasks")}
              footer={
                team.overdue > 0
                  ? `Team-wide: ${fmtInt(team.overdue)} overdue, ${fmtInt(team.dueToday)} due today, ${fmtInt(team.nextSevenDays)} in the next 7 days.`
                  : undefined
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {[...mineOverdue, ...mineToday].slice(0, 3).map((task) => {
                  const isLate = Boolean(task.DueDate) && task.DueDate < (weekly?.today || "");
                  return (
                    <MiniRow
                      key={task.id}
                      primary={task.Title}
                      secondary={
                        task.DueDate
                          ? `Due ${fmtDate(task.DueDate)} · ${task.Priority}`
                          : `${task.Priority} · no due date`
                      }
                      trailing={isLate ? "Late" : "Today"}
                      trailingColor={
                        isLate ? theme.palette.error.main : theme.palette.warning.main
                      }
                    />
                  );
                })}
                {mineCount === 0
                  ? (team.items || []).slice(0, 3).map((task) => (
                      <MiniRow
                        key={task.id}
                        primary={task.Title}
                        secondary={`${task.AssignedTo || "Unassigned"} · due ${fmtDate(task.DueDate)}`}
                        trailing="Overdue"
                        trailingColor={theme.palette.error.main}
                        onClick={() => go("/tasks")}
                      />
                    ))
                  : null}
              </Stack>
            </OpsCard>
          </Grid>
        </Grid>
      </>
    );
  };

  // ── THIS WEEK: what happened ─────────────────────────────────────────────
  // ── This month ───────────────────────────────────────────────────────────
  // Month-close view: what the month contained, how much of it is closed out,
  // and which data-integrity checks fired on it. Reuses the money block because
  // "what is still unbilled" is the other month-end question.
  const renderThisMonth = () => {
    if (!monthStats) {
      return (
        <>
          <TimeSection label="This month" range="loading…" accent={theme.palette.info.main} />
          <Grid container spacing={2}>
            {[...Array(3)].map((_, index) => (
              <Grid key={index} size={{ xs: 12, md: 6, lg: 4 }}>
                <Skeleton variant="rounded" height={172} sx={{ borderRadius: "12px" }} animation="wave" />
              </Grid>
            ))}
          </Grid>
        </>
      );
    }

    const monthAccent =
      monthStats.open > 0 ? theme.palette.warning.main : theme.palette.success.main;

    // One button per service list, each scoped to THIS month. `ids` is empty on
    // purpose — the number is the whole month, so the destination is the month,
    // and `categoryOptionUrl` turns that into `?months=YYYY-MM`.
    const monthCategoryOptions = Object.keys(CATEGORY_TARGET)
      .map((key) => ({
        key,
        path: CATEGORY_TARGET[key].path,
        label: CATEGORY_TARGET[key].label,
        count: Number(monthStats.byCategory?.[key] || 0),
        ids: [],
        months: [monthStats.monthKey],
      }))
      .filter((option) => option.count > 0)
      .sort((a, b) => b.count - a.count);

    return (
      <>
        <TimeSection
          label={monthStats.label}
          range={
            monthStats.total > 0
              ? `${fmtInt(monthStats.total)} assignments · ${fmtInt(monthStats.open)} still open`
              : "nothing booked"
          }
          accent={monthAccent}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<DateRangeRounded />}
              accent={theme.palette.info.main}
              title="Booked this month"
              value={fmtInt(monthStats.total)}
              caption={`${fmtInt(monthStats.done)} closed · ${fmtInt(monthStats.open)} still open${
                monthStats.cancelled ? ` · ${fmtInt(monthStats.cancelled)} cancelled` : ""
              }`}
              // Month-scoped split: every row here is dated inside this month, so
              // each list opens on that month. No ids — the count is the whole
              // month, not a hand-picked subset.
              categoryOptions={monthCategoryOptions}
              onOpenCategory={openCategory}
              footer="Counts every assignment dated inside this month."
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <OpsCard
              icon={<FactCheckRounded />}
              accent={
                monthStats.errorChecks > 0
                  ? theme.palette.error.main
                  : monthStats.warningChecks > 0
                    ? theme.palette.warning.main
                    : theme.palette.success.main
              }
              title="Data integrity this month"
              value={fmtInt(monthStats.errorChecks + monthStats.warningChecks)}
              caption={
                monthStats.checksThisMonth.length === 0
                  ? "no checks fired on this month"
                  : `${monthStats.errorChecks} error · ${monthStats.warningChecks} warning check${
                      monthStats.errorChecks + monthStats.warningChecks === 1 ? "" : "s"
                    }`
              }
              footer="Same engine the Checks column uses."
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <OpsCard
              icon={<DonutLargeRounded />}
              accent={theme.palette.primary.main}
              title="Mix by service type"
              value={fmtInt(monthStats.byCategory.Interpretation)}
              caption={`Interpretations · ${fmtInt(monthStats.byCategory.Translation)} translations · ${fmtInt(
                monthStats.byCategory.OtherService,
              )} other`}
              categoryOptions={monthCategoryOptions}
              onOpenCategory={openCategory}
              footer="Headline number is interpretations."
            />
          </Grid>

          {monthStats.checksThisMonth.slice(0, 4).map((check) => (
            <Grid key={check.code} size={{ xs: 12, md: 6, lg: 4 }}>
              <OpsCard
                icon={check.severity === "error" ? <ErrorOutlineRounded /> : <WarningAmberRounded />}
                accent={
                  check.severity === "error"
                    ? theme.palette.error.main
                    : theme.palette.warning.main
                }
                title={check.label}
                value={fmtInt(check.count)}
                caption={check.secondary || check.severity}
                onClick={() => go(checkLink(check))}
                footer="Open the rows this check is about."
              />
            </Grid>
          ))}
        </Grid>

        {renderMoney()}
      </>
    );
  };

  const renderThisWeek = () => {
    if (isWeeklyLoading && !weekly) {
      return (
        <>
          <TimeSection
            label="This week"
            range="loading…"
            accent={theme.palette.info.main}
          />
          <Grid container spacing={2}>
            {[...Array(3)].map((_, index) => (
              <Grid key={index} size={{ xs: 12, md: 6, lg: 4 }}>
                <Skeleton variant="rounded" height={172} sx={{ borderRadius: "12px" }} animation="wave" />
              </Grid>
            ))}
          </Grid>
        </>
      );
    }

    if (!weekly) {
      return (
        <>
          <TimeSection label="This week" range="unavailable" accent={theme.palette.text.secondary} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <OpsCard
                icon={<DateRangeRounded />}
                accent={theme.palette.text.secondary}
                title="Week figures unavailable"
                value="—"
                caption="The week summary could not load. The Today section above is still accurate."
                onClick={fetchWeekly}
                footer="Click to retry."
              />
            </Grid>
          </Grid>
        </>
      );
    }

    const week = weekly.thisWeek;
    const volume = week.volume || {};
    const interp = week.interpretations || {};
    const completed = week.completed || {};
    const deliveries = week.deliveries || {};
    const money = week.money;

    // Late deliveries were already counted by the server; these are their rows,
    // split per list. Count comes from the ids (that IS the number the card
    // shows), and only "late" is meaningful as a destination — the on-time half
    // has nothing to fix.
    const deliveryLateOptions = categoryOptionsOf(deliveries.byCategory).map(
      (option) => ({
        ...option,
        count: option.ids.length || option.count,
      }),
    );

    const openAfterWeek = Math.max(
      0,
      Number(volume.booked || 0) - Number(volume.delivered || 0) - Number(volume.cancelled || 0),
    );

    return (
      <>
        <TimeSection
          label={`${fmtDate(week.start)} → ${fmtDate(week.end)}`}
          range={`${week.daysElapsed} of 7 days elapsed`}
          accent={theme.palette.info.main}
          chips={[
            {
              key: "booked",
              label: `${fmtInt(volume.booked)} booked`,
            },
            {
              key: "done",
              label: `${fmtInt(volume.delivered)} finished`,
              tone:
                Number(volume.delivered) > 0
                  ? theme.palette.success.main
                  : undefined,
            },
            openAfterWeek > 0
              ? {
                  key: "open",
                  label: `${fmtInt(openAfterWeek)} still open`,
                  tone: theme.palette.warning.main,
                }
              : null,
            Number(volume.cancelled) > 0
              ? {
                  key: "cancelled",
                  label: `${fmtInt(volume.cancelled)} cancelled`,
                }
              : null,
          ]}
        />

        <Grid container spacing={2}>
          {/* Volume + the seven-day shape */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <OpsCard
              icon={<DateRangeRounded />}
              accent={theme.palette.info.main}
              title="Work this week"
              value={fmtInt(volume.booked)}
              caption={`${fmtInt(volume.delivered)} finished · ${fmtInt(volume.stillOpen)} still open · ${fmtInt(volume.cancelled)} cancelled`}
              footer={`Interpretations ${fmtInt(volume.byCategory?.Interpretation)} · Translations ${fmtInt(volume.byCategory?.Translation)} · Other ${fmtInt(volume.byCategory?.Other)}`}
            >
              <Box sx={{ mt: 1.5 }}>
                <WeekStrip
                  days={week.byDay || []}
                  series={[
                    { key: "booked", label: "Booked", color: theme.palette.info.main },
                    { key: "delivered", label: "Finished", color: theme.palette.success.main },
                  ]}
                  emptyHint="Nothing booked"
                />
              </Box>
            </OpsCard>
          </Grid>

          {/* Interpretations closed vs still open */}
          <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
            <OpsCard
              icon={<RecordVoiceOverRounded />}
              accent={
                Number(interp.outstanding) > 0
                  ? theme.palette.warning.main
                  : theme.palette.success.main
              }
              title="Interpretations closed"
              value={
                interp.completionRate === null
                  ? "—"
                  : `${interp.completionRate}%`
              }
              valueColor={
                interp.completionRate === null
                  ? theme.palette.text.secondary
                  : interp.completionRate >= 90
                    ? theme.palette.success.main
                    : interp.completionRate >= 70
                      ? theme.palette.warning.main
                      : theme.palette.error.main
              }
              caption={`${fmtInt(interp.completed)} of ${fmtInt(interp.total)} marked Completed`}
              // These figures describe THIS WEEK's interpretations, so the link
              // carries the week's own rows — not the month, and certainly not
              // an unfiltered list that would contradict the numbers above.
              onClick={() => go(buildListUrl("/interpretations", interp.filters))}
              footer={`${fmtInt(interp.outstanding)} still open · ${fmtInt(interp.cancelled)} cancelled · ${fmtInt(interp.mode?.inPerson)} in-person, ${fmtInt(interp.mode?.virtual)} virtual`}
            />
          </Grid>

          {/* On-time delivery — explicitly shows its sample size */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<ScheduleRounded />}
              accent={
                deliveries.onTimeRate === null
                  ? theme.palette.text.secondary
                  : deliveries.onTimeRate >= 90
                    ? theme.palette.success.main
                    : deliveries.onTimeRate >= 70
                      ? theme.palette.warning.main
                      : theme.palette.error.main
              }
              title="On-time delivery"
              value={
                deliveries.onTimeRate === null ? "—" : `${deliveries.onTimeRate}%`
              }
              valueColor={
                deliveries.onTimeRate === null
                  ? theme.palette.text.secondary
                  : undefined
              }
              caption={
                deliveries.measured > 0
                  ? `${fmtInt(deliveries.onTime)} on time · ${fmtInt(deliveries.late)} late`
                  : "Not enough delivery dates recorded to measure"
              }
              // The card is ABOUT the late rows, and the server already sends
              // their ids — so the click shows exactly those. Late deliveries
              // are Translation or Other Service, so one button per list.
              categoryOptions={deliveryLateOptions}
              onOpenCategory={openCategory}
              // The sample size is the honest part: DeliveryDate is only kept
              // on some rows, so a bare percentage would overstate confidence.
              footer={
                deliveries.measured > 0
                  ? `Measured on ${fmtInt(deliveries.measured)} assignment(s) with both a deadline and a delivery date.`
                  : "Record a delivery date to start measuring this."
              }
            >
              {(deliveries.worst || []).length > 0 ? (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {deliveries.worst.slice(0, 3).map((row) => (
                    <MiniRow
                      key={row.id}
                      primary={`#${row.ProjectNumber ?? "—"} · ${row.ClientName}`}
                      secondary={`Due ${fmtDate(row.Deadline)} · delivered ${fmtDate(row.DeliveryDate)}`}
                      trailing={`${fmtInt(row.daysLate)}d late`}
                      trailingColor={theme.palette.error.main}
                      onClick={() => openService(row.id, row.ServiceCategory)}
                    />
                  ))}
                </Stack>
              ) : null}
            </OpsCard>
          </Grid>

          {/* Finished by category — answers "what kind of work did we close?" */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<CheckCircleRounded />}
              accent={theme.palette.success.main}
              title="Finished this week"
              value={fmtInt(completed.total)}
              valueColor={
                completed.total > 0
                  ? theme.palette.success.main
                  : theme.palette.text.secondary
              }
              caption="Delivered or Completed, by the assignment date"
              footer={`Interpretations ${fmtInt(completed.byCategory?.Interpretation)} · Translations ${fmtInt(completed.byCategory?.Translation)} · Other ${fmtInt(completed.byCategory?.Other)}`}
            >
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {completed.withDeliveryDate > 0
                    ? `${fmtInt(completed.withDeliveryDate)} of ${fmtInt(completed.total)} have a delivery date on record.`
                    : "None of these carry a delivery date, so on-time cannot be judged for them."}
                </Typography>
              </Box>
            </OpsCard>
          </Grid>

          {/* Miles — real operational cost hiding in a rarely-opened column */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<RouteRounded />}
              accent={theme.palette.info.main}
              title="Miles driven"
              value={`${fmtInt(week.mileage?.miles)} mi`}
              caption={`across ${fmtInt(week.mileage?.assignments)} assignment(s) this week`}
              footer="Mileage recorded on work dated this week."
            />
          </Grid>

          {/* Money — Admin only, and the server omits it for Members */}
          {money ? (
            <Grid size={{ xs: 12, lg: 4 }}>
              <OpsCard
                icon={<AttachMoneyRounded />}
                accent={theme.palette.primary.main}
                title="Booked this week"
                value={fmtMoney(money.bookedCharge)}
                caption={`${fmtMoney(money.bookedProfit)} profit${
                  money.bookedMargin === null ? "" : ` · ${money.bookedMargin}% margin`
                }`}
                onClick={() => go("/reports")}
                footer={`Cost ${fmtMoney(money.bookedCost)} · linguist pay accrued ${fmtMoney(
                  money.linguistPayAccrued,
                )}`}
              />
            </Grid>
          ) : null}
        </Grid>
      </>
    );
  };

  // ── NEXT WEEK: what is coming ────────────────────────────────────────────
  const renderNextWeek = () => {
    // This used to `return null` while the weekly payload was in flight, so the
    // whole tab was BLANK — indistinguishable from a broken screen. A skeleton
    // with its own heading makes the wait legible.
    if (isWeeklyLoading && !weekly) {
      return (
        <>
          <TimeSection label="Next week" range="loading…" accent={theme.palette.info.main} />
          {renderCardSkeletons(3, 172)}
        </>
      );
    }

    if (!weekly) {
      return (
        <>
          <TimeSection label="Next week" range="unavailable" accent={theme.palette.text.secondary} />
        </>
      );
    }

    const week = weekly.nextWeek;
    const unassigned = week.unassigned || {};
    const deadlines = week.deadlines || {};
    const atRisk = deadlines.atRisk || {};
    const demand = week.demand || {};
    const offers = week.offers || {};

    const busiestDay = [...(week.byDay || [])].sort((a, b) => b.total - a.total)[0];

    // At-risk work is either a Translation or an Other Service, and those are
    // different screens. Both counts are shown, so both lists must be offered —
    // picking the larger one silently hid the other half of the number.
    const atRiskOptions = categoryOptionsOf(
      atRisk.categoryRefs || [
        atRisk.byCategory?.Translation
          ? { category: "Translation", ...atRisk.byCategory.Translation }
          : null,
        atRisk.byCategory?.Other
          ? { category: "OtherService", ...atRisk.byCategory.Other }
          : null,
      ],
    );
    // Count is attached explicitly: a FilterRef carries ids/months, not a count.
    const atRiskOptionsWithCounts = atRiskOptions.map((option) => ({
      ...option,
      count: Number(
        option.key === "Translation"
          ? atRisk.counts?.Translation || 0
          : atRisk.counts?.Other || 0,
      ),
    }));

    return (
      <>
        <TimeSection
          label={`${fmtDate(week.start)} → ${fmtDate(week.end)}`}
          range={
            week.total === 0
              ? "nothing booked yet"
              : busiestDay && busiestDay.total > 0
                ? `busiest ${busiestDay.weekday} (${busiestDay.total})`
                : undefined
          }
          accent={theme.palette.success.main}
          chips={[
            { key: "total", label: `${fmtInt(week.total)} booked` },
            Number(unassigned.count) > 0
              ? {
                  key: "unassigned",
                  label: `${fmtInt(unassigned.count)} unassigned`,
                  tone: theme.palette.error.main,
                  onClick: () =>
                    go(buildListUrl("/interpretations", unassigned.filters)),
                }
              : null,
            Number(atRisk.count) > 0
              ? {
                  key: "atRisk",
                  label: `${fmtInt(atRisk.count)} at risk`,
                  tone: theme.palette.warning.main,
                  options: atRiskOptionsWithCounts,
                  onSelect: openCategory,
                }
              : null,
            Number(week.doubleBookingCount) > 0
              ? {
                  key: "double",
                  label: `${fmtInt(week.doubleBookingCount)} double-booked`,
                  tone: theme.palette.error.main,
                }
              : null,
          ]}
        />

        <Grid container spacing={2}>
          {/* The week shape */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <OpsCard
              icon={<EventRounded />}
              accent={theme.palette.success.main}
              title="Scheduled next week"
              value={fmtInt(week.total)}
              valueColor={
                week.total > 0
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary
              }
              caption={
                week.total === 0
                  ? "Nothing is booked for next week yet"
                  : `${fmtInt(week.byCategory?.Interpretation)} interpretations · ${fmtInt(
                      week.byCategory?.Translation,
                    )} translations · ${fmtInt(week.byCategory?.Other)} other`
              }
              footer={
                (week.emptyDays || []).length > 0
                  ? `Days with nothing booked: ${(week.emptyDays || [])
                      .map((date) => fmtDate(date))
                      .join(", ")}`
                  : "Every day next week has work booked."
              }
            >
              <Box sx={{ mt: 1.5 }}>
                <WeekStrip
                  days={week.byDay || []}
                  series={[
                    {
                      key: "interpretations",
                      label: "Interpretations",
                      color: theme.palette.primary.main,
                    },
                    {
                      key: "translations",
                      label: "Translations",
                      color: theme.palette.info.main,
                    },
                    {
                      key: "other",
                      label: "Other services",
                      color: theme.palette.warning.main,
                    },
                  ]}
                  emptyHint="Nothing booked"
                />
              </Box>
            </OpsCard>
          </Grid>

          {/* Unassigned — the most dangerous state */}
          <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
            <OpsCard
              icon={<PersonOffRounded />}
              accent={
                unassigned.count > 0
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
              title="Unassigned next week"
              value={fmtInt(unassigned.count)}
              valueColor={
                unassigned.count > 0
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
              caption="Confirmed or requested, nobody on the hook yet"
              onClick={() =>
                go(buildListUrl("/interpretations", unassigned.filters || { ids: [] }))
              }
              footer={
                unassigned.count === 0
                  ? "Everyone booked next week has a linguist."
                  : "These cannot be delivered as they stand."
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(unassigned.items || []).slice(0, 3).map((item) => (
                  <MiniRow
                    key={item.id}
                    primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                    secondary={`${fmtDate(item.Date)}${item.TimeStart ? ` · ${fmtTime(item.TimeStart)}` : ""} · ${item.Status}`}
                    trailing={item.daysAway === 0 ? "Today" : `in ${fmtInt(item.daysAway)}d`}
                    trailingColor={theme.palette.error.main}
                    onClick={() => openService(item.id, item.ServiceCategory)}
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          {/* Deadlines due next week */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<EventBusyRounded />}
              accent={
                atRisk.count > 0 ? theme.palette.warning.main : theme.palette.success.main
              }
              title="Deadlines next week"
              value={fmtInt(deadlines.count)}
              caption={
                atRisk.count > 0
                  ? `${fmtInt(atRisk.count)} already at risk (3 days or less)`
                  : "None of them are close yet"
              }
              categoryOptions={atRiskOptionsWithCounts}
              onOpenCategory={openCategory}
              footer={`At risk = deadline lands next week, work still open, three days or fewer to turn it around. Translations ${fmtInt(atRisk.counts?.Translation)} · Other ${fmtInt(atRisk.counts?.Other)}`}
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(atRisk.items || []).slice(0, 3).map((item) => (
                  <MiniRow
                    key={item.id}
                    primary={`#${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                    secondary={`Due ${fmtDate(item.Deadline)} · ${item.Name || item.ServiceCategory}`}
                    trailing={`${fmtInt(item.daysLeft)}d`}
                    trailingColor={theme.palette.warning.main}
                    onClick={() => openService(item.id, item.ServiceCategory)}
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          {/* Demand — who and what next week leans on */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<GroupsRounded />}
              accent={theme.palette.primary.main}
              title="Demand next week"
              caption="Who and what the week leans on"
              footer="Counts of scheduled assignments, not money."
            >
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {[
                  { label: "Clients", rows: demand.clients || [] },
                  { label: "Linguists", rows: demand.linguists || [] },
                  { label: "Languages", rows: demand.languages || [] },
                ].map((group) => (
                  <Box key={group.label}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        fontSize: "0.62rem",
                        letterSpacing: 0.4,
                      }}
                    >
                      {group.label}
                    </Typography>
                    {group.rows.length === 0 ? (
                      <EmptyLine>Nothing scheduled</EmptyLine>
                    ) : (
                      group.rows.slice(0, 3).map((row) => (
                        <MiniRow
                          key={`${group.label}-${row.name}`}
                          primary={row.name}
                          trailing={fmtInt(row.count)}
                        />
                      ))
                    )}
                  </Box>
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          {/* Double bookings — only a human can judge if back-to-back is fine */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<WarningAmberRounded />}
              accent={
                week.doubleBookingCount > 0
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
              title="Double-booked"
              value={fmtInt(week.doubleBookingCount)}
              valueColor={
                week.doubleBookingCount > 0
                  ? theme.palette.error.main
                  : theme.palette.success.main
              }
              caption="Same linguist, overlapping times"
              footer={
                week.doubleBookingCount === 0
                  ? "No overlapping bookings next week."
                  : "Back-to-back is fine; overlapping is not — check each one."
              }
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(week.doubleBookings || []).map((clash, index) => (
                  <MiniRow
                    key={`${clash.linguistName}-${clash.date}-${index}`}
                    primary={`${clash.linguistName} · ${fmtDate(clash.date)}`}
                    secondary={`#${clash.first.ProjectNumber ?? "—"} and #${clash.second.ProjectNumber ?? "—"}`}
                    trailing="Clash"
                    trailingColor={theme.palette.error.main}
                    onClick={() => openService(clash.first.id, clash.first.ServiceCategory)}
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          {/* Offers still unanswered */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <OpsCard
              icon={<HourglassEmptyRounded />}
              accent={
                offers.expiringNextWeek > 0
                  ? theme.palette.warning.main
                  : theme.palette.primary.main
              }
              title="Offers awaiting reply"
              value={fmtInt(offers.pending)}
              caption={
                offers.expiringNextWeek > 0
                  ? `${fmtInt(offers.expiringNextWeek)} expire before next week ends`
                  : "None expiring next week"
              }
              // Offers belong to assignments, so opening the list they live in
              // (per category) is the honest destination — there is no
              // portal-wide offers screen to link to.
              categoryOptions={categoryOptionsOf(offers.byCategory)}
              onOpenCategory={openCategory}
              footer="An unanswered offer is not a booking — the slot is still open."
            >
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {(offers.items || []).slice(0, 3).map((offer) => (
                  <MiniRow
                    key={offer.OfferID ?? offer.id}
                    primary={offer.linguistName}
                    secondary={`Offered ${fmtDate(offer.offeredAt)}`}
                    trailing={`exp ${fmtDate(offer.expiresAt)}`}
                    trailingColor={theme.palette.warning.main}
                    // Offer rows now carry their assignment id + category, so a
                    // pending offer opens the assignment it is about. Before,
                    // they were inert (the payload only had the OFFER id, which
                    // is not a valid profile id).
                    onClick={
                      offer.id
                        ? () => openService(offer.id, offer.ServiceCategory)
                        : undefined
                    }
                  />
                ))}
              </Stack>
            </OpsCard>
          </Grid>

          {/* Expected revenue — Admin only, and labelled as expected */}
          {week.money ? (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <OpsCard
                icon={<AttachMoneyRounded />}
                accent={theme.palette.primary.main}
                title="Expected next week"
                value={fmtMoney(week.money.expectedCharge)}
                caption="Account charges already booked"
                onClick={() => go("/reports")}
                footer="What is on the books, not a forecast — profit is not estimated before the work happens."
              />
            </Grid>
          ) : null}
        </Grid>
      </>
    );
  };

  const renderSchedule = () => {
    // Skeleton, not "Nothing scheduled" — showing an empty card while the
    // request is still in flight reads as a real answer and is worse than no
    // answer at all.
    if (!summary) {
      return (
        <>
          <SectionTitle hint={dayjs().format("dddd, MMM D")}>Today's schedule</SectionTitle>
          {renderCardSkeletons(1, 132)}
        </>
      );
    }
    const schedule = summary?.schedule || { count: 0, items: [] };

    return (
      <>
        <SectionTitle hint={dayjs().format("dddd, MMM D")}>Today's schedule</SectionTitle>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}>
            <OpsCard
              icon={<EventRounded />}
              accent={schedule.count > 0 ? theme.palette.primary.main : theme.palette.success.main}
              title="Interpretations today"
              value={fmtInt(schedule.count)}
              caption={
                schedule.count === 0
                  ? "Nothing scheduled"
                  : schedule.next
                    ? `Next: ${fmtTime(schedule.next.TimeStart)} · #${schedule.next.ProjectNumber ?? "—"} ${schedule.next.ClientName}`
                    : "No start time recorded"
              }
              onClick={() => go("/calendar")}
              footer={
                schedule.unassignedCount > 0
                  ? `${schedule.unassignedCount} of today's assignments have no linguist yet.`
                  : undefined
              }
            >
              {(schedule.items || []).length > 0 ? (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {schedule.items.slice(0, 6).map((item) => (
                    <MiniRow
                      key={item.id}
                      primary={`${fmtTime(item.TimeStart) || "—"} · #${item.ProjectNumber ?? "—"} · ${item.ClientName}`}
                      secondary={`${item.Details || "Interpretation"}${item.Mode ? ` · ${item.Mode}` : ""}`}
                      trailing={item.hasLinguist ? item.Status : "No linguist"}
                      trailingColor={
                        item.hasLinguist ? theme.palette.text.secondary : theme.palette.error.main
                      }
                      onClick={() => openService(item.id, item.ServiceCategory)}
                    />
                  ))}
                </Stack>
              ) : null}
            </OpsCard>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Box
      p={{ xs: "0.5rem 0.75rem", sm: "1rem 1rem" }}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
      }}
    >
      {/* Header — deliberately NO page title. `usePageTitle("Dashboard")` already
          puts the name in the Navbar, exactly like every other screen in the
          portal (verified: Clients / Interpretations / Invoices render no h4/h5
          of their own).
          2026-11-03 (Amine): the "Updated 1:50 PM · America/New_York" line and
          this whole header row are GONE. The timestamp repeated itself on every
          refresh and the timezone means nothing to a single-timezone team; the
          refresh action now sits at the far right of the tab strip, which is the
          only thing that actually needed a home. */}

      {error ? (
        <Alert
          severity="error"
          sx={{ my: 1 }}
          action={
            <Button color="inherit" size="small" onClick={fetchSummary}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pb: 2 }}>
        {/* PROGRESSIVE FILL-IN (2026-11-03, Amine). This used to be
            `isLoading && !summary ? fullPageSkeleton : summary ? … : null`,
            which meant the tab bar, the ribbon and every heading were withheld
            until the slowest request finished — the screen simply did not
            respond, which is what "freezing in place" looked like.
            The chrome now paints on the first frame; each section owns its
            skeleton (renderCardSkeletons) and fills in as its data arrives. */}
        <Box>
            {/* 2026-11-03 (Amine): STICKY header block.
                The tabs and the attention ribbon pin to the top of the scroll
                area while the tab body scrolls underneath, so the signals never
                scroll out of sight and the tab bar stays reachable on a long
                tab. The background is opaque (`background.alt`, the page
                colour) or the scrolling content would show through. */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                bgcolor: theme.palette.background.alt,
                pt: 0.5,
                pb: 0.5,
                mt: -0.5,
              }}
            >
              {/* 2026-11-03 (Amine): tabs and the refresh action share one row.
                  The Refresh button is intentionally OUTSIDE the Tabs element so
                  it cannot scroll away with the tab strip — the tabs scroll, the
                  button stays pinned right. On mobile it collapses to an
                  icon-only 40×40 button so the four tab labels keep their room.
                  Border/shadow sit on this wrapper (not on Tabs) so the pill and
                  the button read as a single control. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  mb: signals.length > 0 || criticalSignals.length > 0 ? 1 : 0,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  backgroundColor: theme.palette.background.default,
                  boxShadow: theme.shadows[1],
                  // 2026-11-03 (Amine): 1.5 (12px) — NOT 0.5. The card's own
                  // corner radius is 12px, so anything less lets the selected
                  // tab's rounded corner sit INSIDE the card's curve. That is
                  // what made the strip look clipped against the left edge even
                  // though padding was present: at 8px total inset the blue
                  // corner was still under the curve. 12px clears it exactly.
                  p: 1.5,
                  minWidth: 0,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    // 36, not 40/44: the tab stretches to fill this, so a taller
                    // strip would add height for nothing now that the vertical
                    // inset comes from the wrapper's 12px padding.
                    minHeight: 36,
                    borderRadius: "10px",
                    // ⚠️ The theme sets `MuiTabs.root` to
                    // `backgroundColor: bg_light (#ffffff)` + `borderBottom:
                    // 1px solid`, which is invisible when the Tabs element IS
                    // the card (as it used to be here) but becomes a SECOND
                    // white card stacked inside the wrapper once the wrapper
                    // owns the surface. Neutralised explicitly so the wrapper
                    // is the only card.
                    backgroundColor: "transparent",
                    borderBottom: "none",
                    "& .MuiTabs-indicator": { display: "none" },
                    // The wrapper's 12px padding provides the inset, so the
                    // scroller needs none — and padding here proved unreliable
                    // anyway (MUI's scrollable scroller scrolls its own content,
                    // so a small pad was invisible).
                    "& .MuiTabs-scroller": { borderRadius: "10px" },
                    "& .MuiTab-root": {
                      textTransform: "none",
                      minHeight: 36,
                      // Vertical inset now comes from the wrapper padding;
                      // doubling it here would make the strip taller than the
                      // Refresh button for no benefit.
                      my: 0,
                      px: { xs: 1, sm: 2 },
                      minWidth: 0,
                      borderRadius: "10px",
                      fontWeight: 600,
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      color: theme.palette.text.secondary,
                      transition: "background-color 0.15s ease, color 0.15s ease",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    },
                    "& .Mui-selected": {
                      color: `${theme.palette.primary.main} !important`,
                      backgroundColor: theme.palette.primary.buttonHover,
                    },
                  }}
                >
                  <Tab value="today" label="Today" />
                  <Tab value="week" label="This week" />
                  <Tab value="next" label="Next week" />
                  <Tab value="month" label="This month" />
                </Tabs>

                <Tooltip title={isLoading ? "Refreshing…" : "Refresh"}>
                  <span>
                    <Button
                      size="small"
                      variant={isMobile ? "text" : "outlined"}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <RefreshRounded fontSize="small" />
                        )
                      }
                      onClick={fetchSummary}
                      disabled={isLoading}
                      aria-label="Refresh dashboard"
                      sx={{
                        flexShrink: 0,
                        minWidth: 0,
                        px: isMobile ? 1 : 1.5,
                        py: 0.5,
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.border2,
                        "& .MuiButton-startIcon": {
                          mr: isMobile ? 0 : 1,
                          ml: 0,
                        },
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.buttonHover,
                        },
                      }}
                    >
                      {isMobile ? null : "Refresh"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {/* Attention ribbon — compact single strip so pinning it costs
                  one row of height, not a full card.
                  2026-11-03 (Amine): the 1px outline was `primary.border2`
                  (#F0F0F0 in light mode) — effectively white — so the ribbon
                  read as a stray white box. Removed: the tinted background and
                  the left accent bar already define it. `elevation={0}` drops
                  Paper's shadow ring for the same reason.
                  The ribbon reads `summary.deadlines`, so before the summary
                  lands it shows a same-height skeleton: "Nothing overdue" while
                  still loading would be a false all-clear. */}
              {!summary ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1, sm: 1.25 },
                    borderRadius: "12px",
                    bgcolor: theme.palette.background.default,
                    borderLeft: `4px solid ${theme.palette.primary.border2}`,
                  }}
                >
                  <Skeleton variant="text" width="55%" height={24} />
                </Paper>
              ) : (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  borderRadius: "12px",
                  bgcolor: ribbonTone.bg,
                  borderLeft: `4px solid ${ribbonTone.color}`,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Box sx={{ color: ribbonTone.color, display: "flex" }}>
                    {ribbonTone.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {criticalSignals.length > 0
                      ? `${criticalSignals.length} critical`
                      : warningSignals.length > 0
                        ? `${warningSignals.length} to clean up`
                        : "Nothing overdue"}
                  </Typography>

                  {criticalSignals.length === 0 && warningSignals.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {fmtInt(summary.deadlines?.totals?.dueToday)} due today ·{" "}
                      {fmtInt(summary.deadlines?.totals?.next7)} next 7 days
                    </Typography>
                  ) : (
                    signals.map((signal) => (
                      <SplitChip
                        key={signal.key}
                        label={signal.label}
                        options={signal.options}
                        tone={
                          signal.severity === "error"
                            ? theme.palette.error.main
                            : signal.severity === "warning"
                              ? theme.palette.warning.main
                              : theme.palette.info.main
                        }
                        onSelect={signal.onSelect}
                        onClick={signal.onClick}
                      />
                    ))
                  )}
                </Stack>
              </Paper>
              )}
            </Box>

            {activeTab === "today" && (
              <>
                {/* The tab already says "Today", so the band header leads with
                    the DATE — the thing a reader actually needs — instead of
                    repeating the word.
                    Before the summary lands the date is unknown, so the header
                    falls back to the local date rather than showing Invalid
                    Date or hiding the band entirely. */}
                <TimeSection
                  label={
                    summary?.today
                      ? dayjs(summary.today).format("dddd, MMMM D")
                      : dayjs().format("dddd, MMMM D")
                  }
                  range={
                    !summary
                      ? "loading…"
                      : criticalSignals.length > 0
                        ? `${criticalSignals.length} critical`
                        : warningSignals.length > 0
                          ? `${warningSignals.length} to clean up`
                          : "nothing overdue"
                  }
                  accent={
                    !summary
                      ? theme.palette.primary.main
                      : criticalSignals.length
                        ? theme.palette.error.main
                        : warningSignals.length
                          ? theme.palette.warning.main
                          : theme.palette.success.main
                  }
                />

                {renderSchedule()}
                {renderCommitments()}
                {renderAboutToBreak()}
                {renderMyTasks()}
                {renderMoney()}
                {renderIntegrity()}
              </>
            )}

            {activeTab === "week" && <>{renderThisWeek()}</>}
            {activeTab === "next" && <>{renderNextWeek()}</>}
            {activeTab === "month" && <>{renderThisMonth()}</>}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard_NEW;
